import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#F59E0B", "#06B6D4", "#8B5CF6", "#EF4444", "#10B981", "#EC4899", "#3B82F6"];

export default function ListManager({ kind, title, description, showColor = false, placeholder = "New item", testidPrefix }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const base = `/settings/${kind}`;

  const load = () => api.get(base).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind]);

  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    await api.post(base, { name, color });
    toast.success("Added");
    setName("");
    load();
  };
  const save = async (id) => {
    await api.put(`${base}/${id}`, { name: editName, color: editColor });
    toast.success("Updated");
    setEditingId(null);
    load();
  };
  const remove = async (id) => {
    await api.delete(`${base}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="glass border border-border rounded-2xl p-6" data-testid={`${testidPrefix}-panel`}>
      <h3 className="font-display font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-6">{description}</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6 p-4 bg-secondary/30 rounded-xl">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={placeholder} data-testid={`${testidPrefix}-add-input`} className="flex-1" />
        {showColor && (
          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-offset-background ring-white scale-110" : "hover:scale-110"}`}
                style={{ background: c }} />
            ))}
          </div>
        )}
        <Button onClick={add} data-testid={`${testidPrefix}-add-button`} className="font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>
      </div>

      <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
        {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nothing here yet.</p>}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 border border-border" data-testid={`${testidPrefix}-row`}>
            {editingId === item.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 h-9" data-testid={`${testidPrefix}-edit-input`} />
                {showColor && (
                  <div className="flex items-center gap-1.5">
                    {COLORS.map((c) => (
                      <button key={c} onClick={() => setEditColor(c)} className={`w-6 h-6 rounded-full ${editColor === c ? "ring-2 ring-white" : ""}`} style={{ background: c }} />
                    ))}
                  </div>
                )}
                <Button size="icon" variant="ghost" onClick={() => save(item.id)} data-testid={`${testidPrefix}-save`} className="text-emerald-400"><Check className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                {showColor && <span className="w-4 h-4 rounded-full" style={{ background: item.color }} />}
                <span className="flex-1 font-medium">{item.name}</span>
                <Button size="icon" variant="ghost" onClick={() => { setEditingId(item.id); setEditName(item.name); setEditColor(item.color || COLORS[0]); }} data-testid={`${testidPrefix}-edit`} className="text-muted-foreground hover:text-foreground">
                  <Pencil className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`${testidPrefix}-delete`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="glass border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{item.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>This removes it from the list. Existing items keep their current value.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(item.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
